/**
 * EntityRelationshipCard Component
 * A shared component for displaying entity relationships with color-coded UI
 * Used across detail pages (Customer, Lead, Opportunity, etc.)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building,
  User,
  Target,
  Calendar,
  FileText,
  ArrowRight
} from 'lucide-react';

const ENTITY_RING_CLASSES = {
  Customer: 'ring-blue-300 dark:ring-blue-500',
  Lead: 'ring-green-300 dark:ring-green-500',
  Opportunity: 'ring-purple-300 dark:ring-purple-500',
  Activity: 'ring-orange-300 dark:ring-orange-500',
  SaleNote: 'ring-yellow-200 dark:ring-yellow-400'
};

const CUSTOMER_PERSON_RING_CLASS = 'ring-indigo-300 dark:ring-indigo-500';

const getEntityRingClass = (entityType, customerType) => {
  if (entityType === 'Customer') {
    if (customerType && String(customerType).toLowerCase() === 'person') {
      return CUSTOMER_PERSON_RING_CLASS;
    }
  }
  return ENTITY_RING_CLASSES[entityType] || 'ring-gray-300 dark:ring-gray-500';
};

/**
 * Get entity-specific color scheme
 * @param {string} entityType - Type of entity (Customer, Lead, Opportunity, Activity, SaleNote)
 * @param {string} [customerType] - Optional subtype (Person, Company) for customers
 * @returns {object} Color scheme object
 */
const getEntityColors = (entityType, customerType) => {
  switch (entityType) {
    case 'Customer':
      if (customerType && String(customerType).toLowerCase() === 'person') {
        return {
          bg: 'bg-indigo-50 dark:bg-indigo-900/20',
          border: 'border-indigo-200 dark:border-indigo-800',
          hoverBorder: 'hover:border-indigo-400 dark:hover:border-indigo-600',
          hoverBg: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/30',
          icon: 'text-indigo-600 dark:text-indigo-400',
          badge: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200'
        };
      }
      return {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        hoverBorder: 'hover:border-blue-400 dark:hover:border-blue-600',
        hoverBg: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
        icon: 'text-blue-600 dark:text-blue-400',
        badge: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
      };
    case 'Lead':
      return {
        bg: 'bg-green-50 dark:bg-green-900/20',
        border: 'border-green-200 dark:border-green-800',
        hoverBorder: 'hover:border-green-400 dark:hover:border-green-600',
        hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30',
        icon: 'text-green-600 dark:text-green-400',
        badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
      };
    case 'Opportunity':
      return {
        bg: 'bg-purple-50 dark:bg-purple-900/20',
        border: 'border-purple-200 dark:border-purple-800',
        hoverBorder: 'hover:border-purple-400 dark:hover:border-purple-600',
        hoverBg: 'hover:bg-purple-100 dark:hover:bg-purple-900/30',
        icon: 'text-purple-600 dark:text-purple-400',
        badge: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
      };
    case 'Activity':
      return {
        bg: 'bg-orange-50 dark:bg-orange-900/20',
        border: 'border-orange-200 dark:border-orange-800',
        hoverBorder: 'hover:border-orange-400 dark:hover:border-orange-600',
        hoverBg: 'hover:bg-orange-100 dark:hover:bg-orange-900/30',
        icon: 'text-orange-600 dark:text-orange-400',
        badge: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
      };
    case 'SaleNote':
      return {
        bg: 'bg-yellow-50 dark:bg-yellow-300/20',
        border: 'border-yellow-200 dark:border-yellow-400',
        hoverBorder: 'hover:border-yellow-400 dark:hover:border-yellow-300',
        hoverBg: 'hover:bg-yellow-200 dark:hover:bg-yellow-500/30',
        icon: 'text-yellow-500 dark:text-yellow-200',
        badge: 'bg-yellow-200 dark:bg-yellow-500/20 text-yellow-900 dark:text-yellow-100'
      };
    default:
      return {
        bg: 'bg-gray-50 dark:bg-gray-700',
        border: 'border-gray-200 dark:border-gray-700',
        hoverBorder: 'hover:border-gray-400 dark:hover:border-gray-600',
        hoverBg: 'hover:bg-gray-100 dark:hover:bg-gray-600',
        icon: 'text-gray-600 dark:text-gray-400',
        badge: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
      };
  }
};

/**
 * Get entity-specific icon with colors
 * @param {string} entityType - Type of entity
 * @param {string} size - Icon size class (default: 'h-4 w-4')
 * @param {string} [customerType] - Optional subtype for customer entities
 * @returns {JSX.Element} Icon component
 */
const getEntityIcon = (entityType, size = 'h-4 w-4', customerType) => {
  const colors = getEntityColors(entityType, customerType);
  const iconClass = `${size} ${colors.icon}`;
  
  switch (entityType) {
    case 'Customer':
      return customerType && String(customerType).toLowerCase() === 'person'
        ? <User className={iconClass} />
        : <Building className={iconClass} />;
    case 'Lead': return <User className={iconClass} />;
    case 'Opportunity': return <Target className={iconClass} />;
    case 'Activity': return <Calendar className={iconClass} />;
    case 'SaleNote': return <FileText className={iconClass} />;
    default: return <FileText className={iconClass} />;
  }
};

/**
 * Get navigation path for entity
 * @param {string} entityType - Type of entity
 * @param {number|string} entityId - Entity ID
 * @returns {string} Navigation path
 */
const getEntityPath = (entityType, entityId) => {
  switch (entityType) {
    case 'Customer': return `/customers/${entityId}`;
    case 'Lead': return `/leads/${entityId}`;
    case 'Opportunity': return `/opportunities/${entityId}`;
    case 'Activity': return `/activities/${entityId}`;
    case 'SaleNote': return `/notes?noteId=${entityId}`;
    default: return '#';
  }
};

/**
 * EntityRelationshipCard - Renders a single entity relationship card
 * @param {object} relation - Relation object
 * @param {string} variant - Card variant: 'default', 'compact', 'detailed'
 * @param {function} onClick - Optional click handler (overrides default navigation)
 * @param {function} onClose - Optional close handler (for modals)
 */
const EntityRelationshipCard = ({ 
  relation, 
  variant = 'default',
  onClick,
  onClose,
  showArrow = true,
  className = ''
}) => {
  const navigate = useNavigate();
  const colors = getEntityColors(relation.EntityType, relation.CustomerType);

  const handleClick = () => {
    if (onClick) {
      onClick(relation);
    } else {
      const path = getEntityPath(relation.EntityType, relation.EntityID);
      if (path !== '#') {
        if (onClose) onClose();
        navigate(path);
      }
    }
  };

  // Compact variant (for modals or sidebars)
  if (variant === 'compact') {
    return (
      <div 
        className={`border ${colors.border} ${colors.bg} rounded-lg p-2 ${onClick || getEntityPath(relation.EntityType, relation.EntityID) !== '#' ? 'cursor-pointer' : ''} transition-colors ${className}`}
        onClick={handleClick}
      >
        <div className="flex items-center space-x-2">
          {getEntityIcon(relation.EntityType, 'h-3 w-3')}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${colors.badge}`}>
                {relation.EntityType}
              </span>
              <span className="text-xs text-gray-900 dark:text-white truncate">
                {relation.EntityName || `#${relation.EntityID}`}
              </span>
            </div>
            {relation.RelationType && (
              <div className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                {relation.RelationType}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Detailed variant (for main detail pages with more information)
  if (variant === 'detailed') {
    return (
      <div
        className={`border ${colors.border} ${colors.bg} rounded-lg p-3 ${colors.hoverBorder} cursor-pointer transition-all ${className}`}
        onClick={handleClick}
      >
        <div className="flex items-start space-x-2">
          <div className="mt-0.5 flex-shrink-0">
            {getEntityIcon(relation.EntityType)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate pr-2">
                {relation.EntityName || `${relation.EntityType} #${relation.EntityID}`}
              </p>
              {showArrow && (
                <ArrowRight className={`h-3.5 w-3.5 ${colors.icon} flex-shrink-0 mt-0.5`} />
              )}
            </div>
            <div className="flex items-center space-x-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${colors.badge}`}>
                {relation.EntityType}
              </span>
              {relation.Status && (
                <span className={`px-1.5 py-0.5 text-xs font-semibold rounded ${
                  relation.EntityType === 'Lead' && relation.Status === 'New' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                  relation.EntityType === 'Lead' && relation.Status === 'Qualified' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                  relation.EntityType === 'Lead' && relation.Status === 'Converted' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' :
                  relation.EntityType === 'Opportunity' && relation.Status === 'Closed Won' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                  relation.EntityType === 'Opportunity' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                  'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}>
                  {relation.Status}
                </span>
              )}
              {relation.Amount && (
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {relation.Amount}
                </span>
              )}
            </div>
            {relation.Description && (
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                {relation.Description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default variant (standard card)
  return (
    <div
      className={`flex items-center space-x-3 p-3 rounded-lg border ${colors.border} ${colors.bg} ${colors.hoverBg} cursor-pointer transition-all ${className}`}
      onClick={handleClick}
    >
        {getEntityIcon(relation.EntityType, 'h-4 w-4', relation.CustomerType)}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {relation.EntityName || `${relation.EntityType} #${relation.EntityID}`}
        </p>
        <div className="flex items-center space-x-2 mt-1">
          <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${colors.badge}`}>
            {relation.EntityType}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ID: {relation.EntityID}
          </span>
          {relation.AdditionalInfo && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              • {relation.AdditionalInfo}
            </span>
          )}
        </div>
      </div>
      {showArrow && (
        <ArrowRight className={`h-4 w-4 ${colors.icon} flex-shrink-0`} />
      )}
    </div>
  );
};

/**
 * EntityRelationshipList - Renders a list of entity relationships
 * @param {Array} relations - Array of relation objects
 * @param {string} variant - Card variant to use
 * @param {string} emptyMessage - Message to show when no relations
 * @param {function} onClick - Optional click handler
 * @param {function} onClose - Optional close handler (for modals)
 * @param {string} maxHeight - Maximum height for scrollable list (e.g., 'max-h-96')
 */
export const EntityRelationshipList = ({ 
  relations = [],
  variant = 'default',
  emptyMessage = 'No relationships found',
  onClick,
  onClose,
  showArrow = true,
  maxHeight = 'max-h-[600px]',
  className = ''
}) => {
  if (!relations || relations.length === 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400">{emptyMessage}</p>
    );
  }

  return (
    <div className={`space-y-2 ${maxHeight} overflow-y-auto ${className}`}>
      {relations.map((rel, idx) => (
        <EntityRelationshipCard
          key={rel.RelationID || `${rel.EntityType}-${rel.EntityID}-${idx}`}
          relation={rel}
          variant={variant}
          onClick={onClick}
          onClose={onClose}
          showArrow={showArrow}
        />
      ))}
    </div>
  );
};

// Export utility functions for use in other components
export {
  getEntityColors,
  getEntityIcon,
  getEntityPath,
  getEntityRingClass,
  ENTITY_RING_CLASSES
};

export default EntityRelationshipCard;

